import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import { ValueFormatterParams } from "ag-grid-community";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { downloadCsv } from "utils/csv";
import { CompareChart } from "./CompareChart";
import {
    buildCompareCsvStringFromMerged,
    buildFileName
} from "./compareCsvExport";
import { CompareFilters, SelectOption } from "./CompareFilters";
import { CompareTable } from "./CompareTable";
import {
    CompareColumn,
    CompareTableColumn,
    DEFAULT_ROW_CHECKS,
    MergedRow,
    RowChecks,
    uniqueJoinKeyCount,
    useMergedView
} from "./comparisons";
import { useFilterSlot } from "./FilterSlotContext";

const filterableColumnOptions = <TRow,>(
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): ReadonlyArray<SelectOption> =>
    columns
        .filter(
            (c): c is CompareTableColumn<TRow> & { kind: "compare" } =>
                c.kind === "compare" && c.filterable !== false
        )
        .map((c) => ({ value: c.colId, label: c.headerName }));

const shouldShowPresenceChecks = <TRow,>(
    rows: ReadonlyArray<MergedRow<TRow>>
): boolean => {
    return rows.some((row) => row.onlyIn !== null);
};

export type CompareChartConfig = {
    keyLabel: string;
    /** Show a spinner in place of the plot while rowsA/rowsB are partial. */
    isLoading?: boolean;
};

export type ScopeConfig<TRow> = {
    options: ReadonlyArray<SelectOption>;
    // Returns true if the row is in scope for the given scope value. Compare
    // applies this against the current `selected` scope before merging, so
    // callers can pass raw rowsA/rowsB without pre-filtering.
    apply: (row: TRow, scope: string) => boolean;
};

export type FilterStateProps = {
    text: string;
    setText: (s: string) => void;
    rowChecks: RowChecks;
    setRowChecks: (update: (prev: RowChecks) => RowChecks) => void;
    column: string;
    setColumn: (colId: string) => void;
    showPresence: boolean;
};

const ExportCompareResultsToaster = OverlayToaster.create({
    position: Position.TOP
});

export type ExportOptions = {
    labelA: string;
    labelB: string;
    exportFilename?: string;
    onExportCallback?: (success: boolean, exportFilename: string) => void;
};

export const Compare = <TRow,>({
    rowsA,
    rowsB,
    columns,
    itemLabel,
    scopeOptions,
    chart,
    tableHeight,
    stickyHeader,
    exportOptions,
    totalPending
}: {
    rowsA: ReadonlyArray<TRow>;
    rowsB: ReadonlyArray<TRow>;
    columns: ReadonlyArray<CompareTableColumn<TRow>>;
    itemLabel: string;
    scopeOptions?: ScopeConfig<TRow>;
    chart?: CompareChartConfig;
    tableHeight?: number;
    stickyHeader?: boolean;
    exportOptions?: ExportOptions;
    /** Hide the "of Y" denominator while rowsA/rowsB are still partial. */
    totalPending?: boolean;
}): JSX.Element => {
    const [filterText, setFilterText] = useState("");
    const [rowChecks, setRowChecks] = useState<RowChecks>(DEFAULT_ROW_CHECKS);

    const columnOptions = useMemo(
        () => filterableColumnOptions(columns),
        [columns]
    );

    const [filteredColumn, setFilteredColumn] = useState(
        columnOptions[0].value ?? null
    );

    const defaultScope = scopeOptions?.options?.[0]?.value;
    const [scope, setSelectedScope] = useState<string | undefined>(
        defaultScope
    );

    const scopedRowsA = useMemo(
        () =>
            scopeOptions && scope
                ? rowsA.filter((r) => scopeOptions.apply(r, scope))
                : rowsA,
        [rowsA, scopeOptions?.apply, scope]
    );

    const scopedRowsB = useMemo(
        () =>
            scopeOptions && scope
                ? rowsB.filter((r) => scopeOptions.apply(r, scope))
                : rowsB,
        [rowsB, scopeOptions?.apply, scope]
    );

    const view = useMergedView(
        scopedRowsA,
        scopedRowsB,
        columns,
        rowChecks,
        filterText,
        filteredColumn
    );

    const showPresenceChecks = useMemo(() => {
        return shouldShowPresenceChecks(view.merged);
    }, [view.merged]);

    // "X of Y" denominator: count of unique join keys across rowsA + rowsB
    // before any scope/check/text filtering. Stays anchored as the user
    // narrows scope.
    const total = useMemo(
        () => uniqueJoinKeyCount(rowsA, rowsB, columns),
        [rowsA, rowsB, columns]
    );

    const chartColumn = chart
        ? columns.find(
              (c): c is CompareColumn<TRow> =>
                  c.kind === "compare" && c.colId === filteredColumn
          )
        : undefined;

    const scopeFilterOptions =
        scope && scopeOptions
            ? {
                  selected: scope,
                  options: scopeOptions.options,
                  setScope: setSelectedScope
              }
            : null;

    const onExport = useCallback(async () => {
        if (!exportOptions) return;
        const filename =
            exportOptions.exportFilename ??
            buildFileName(
                { aLabel: exportOptions.labelA, bLabel: exportOptions.labelB },
                "csv",
                itemLabel
            );
        try {
            const csv = await buildCompareCsvStringFromMerged(
                columns,
                view.displayed,
                {
                    aLabel: exportOptions.labelA,
                    bLabel: exportOptions.labelB
                }
            );

            await downloadCsv(csv, filename);
            exportOptions.onExportCallback?.(true, filename);
        } catch (error) {
            ExportCompareResultsToaster.show({
                message: "Failed to export comparison results",
                intent: Intent.DANGER
            });
            exportOptions.onExportCallback?.(false, filename);
        }
    }, [columns, view.displayed, exportOptions, itemLabel]);

    const filterSlot = useFilterSlot();
    const filters = (
        <CompareFilters
            scope={scopeFilterOptions}
            rowChecks={rowChecks}
            setRowChecks={setRowChecks}
            showPresenceFilter={showPresenceChecks}
            rowCounts={view.rowCountsByCol[filteredColumn]}
            filteredColumn={filteredColumn}
            setFilteredColumn={setFilteredColumn}
            columnOptions={columnOptions}
            filterText={filterText}
            setFilterText={setFilterText}
            rendered={view.displayed.length}
            total={totalPending ? undefined : total}
            itemLabel={itemLabel}
            onExport={onExport}
        />
    );

    return (
        <>
            {filterSlot ? createPortal(filters, filterSlot) : filters}

            {chart && chartColumn && (
                <CompareChart<TRow>
                    merged={view.displayed}
                    getValue={chartColumn.getValue}
                    metricLabel={chartColumn.headerName}
                    formatScalar={(v) =>
                        chartColumn.valueFormatter
                            ? chartColumn.valueFormatter({
                                  value: v
                              } as ValueFormatterParams)
                            : String(v)
                    }
                    keyLabel={chart.keyLabel}
                    isLoading={chart.isLoading}
                />
            )}

            <CompareTable<TRow>
                merged={view.displayed}
                columns={columns}
                height={tableHeight}
                stickyHeader={stickyHeader}
            />
        </>
    );
};
